import React, { Component } from "react";
import type { ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import Button from "./ui/Button";
import { Card, CardContent } from "./ui/Card";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-6">
          <Card elevation={3} className="max-w-lg w-full">
            <CardContent padding="large" className="text-center">
              <AlertCircle className="w-16 h-16 text-error mx-auto mb-4" />

              <h2 className="text-2xl font-bold text-error mb-2">
                Une erreur s'est produite
              </h2>

              <p className="text-gray-600 mb-6">
                Nous nous excusons pour le désagrément. Veuillez essayer de
                recharger la page.
              </p>

              {this.state.error && (
                <p className="text-sm text-gray-400 mb-6 font-mono bg-gray-50 p-3 rounded">
                  {this.state.error.message}
                </p>
              )}

              <div className="flex gap-3 justify-center">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.handleReset}
                >
                  Réessayer
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => window.location.reload()}
                >
                  Recharger la page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
